import { Link } from "react-router-dom";
import { ChevronRight, Shield, Truck, Wrench } from "lucide-react";
import { BRANDS, BRANDS_BG } from "../data/site";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";

const brandModels = {
  mahindra: [
    "Bolero", "Scorpio", "XUV300", "XUV500", "XUV700", "Thar",
    "Pik Up", "Marazzo", "TUV300", "Verito", "KUV100"
  ],
  maruti: [
    "Alto", "Swift", "WagonR", "Baleno", "Dzire", "Ertiga",
    "Brezza", "Celerio", "S-Presso", "Eeco", "Ciaz", "Ignis"
  ],
  tata: [
    "Ace", "407", "1109", "1613", "LPT 1512", "LPT 1615",
    "LPT 2515", "LPO 1512", "LPS 4018", "SFC 407", "Ultra",
    "Prima", "Signa", "Starbus"
  ],
};

const Brands = () => (
  <div data-testid="page-brands" className="bg-background">
    {/* HERO */}
    <section className="relative overflow-hidden border-b border-border">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: `url(${BRANDS_BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/50" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20 lg:py-28">
        <Reveal>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-12 bg-[#C8102E]" />
            <span className="text-xs uppercase tracking-overline font-bold text-muted-foreground">
              Featured Brands
            </span>
          </div>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[0.95] max-w-3xl">
            Specialised for <br />
            <span className="text-[#C8102E]">Mahindra · Maruti Suzuki · Tata</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Genuine &amp; high-quality aftermarket components available across
            passenger and commercial ranges.
          </p>
        </Reveal>
      </div>
    </section>

    {/* BRAND CARDS */}
    <section className="py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <StaggerGroup className="grid grid-cols-1 lg:grid-cols-3 gap-0 border-t border-l border-border">
          {BRANDS.map((b, i) => (
            <StaggerItem
              key={b.key}
              className="border-r border-b border-border p-8 lg:p-10 bg-card"
              data-testid={`brand-card-${b.key}`}
            >
              <div className="flex items-baseline gap-3">
                <span className="font-display font-black text-4xl tracking-tighter text-[#C8102E]">
                  0{i + 1}
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>
              <h3 className="mt-8 font-display font-black text-3xl md:text-4xl uppercase tracking-tighter">
                {b.name}
              </h3>
              <p className="mt-3 text-xs uppercase tracking-overline text-muted-foreground">
                {b.tagline}
              </p>
              <p className="mt-6 text-sm text-muted-foreground leading-relaxed">
                {b.description}
              </p>
              <div className="mt-6">
                <div className="text-[11px] uppercase tracking-overline text-muted-foreground mb-3">
                  Supported Models
                </div>
                <div className="flex flex-wrap gap-2">
                  {brandModels[b.key].map((m) => (
                    <span
                      key={m}
                      className="text-[11px] uppercase tracking-overline border border-border px-2 py-1"
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </div>
              <Link
                to={`/contact?brand=${encodeURIComponent(b.name)}`}
                data-testid={`brand-enquire-${b.key}`}
                className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-overline text-[#C8102E] hover:gap-3 transition-all duration-200"
              >
                Enquire for {b.name} <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>

    {/* WHY BRAND-SPECIFIC */}
    <section className="py-20 bg-card">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-border">
          {[
            { Icon: Shield, title: "Genuine Sources", body: "Sourced from OE-authorised channels and trusted aftermarket suppliers." },
            { Icon: Wrench, title: "Fitment Expertise", body: "Brand-specific knowledge to help you pick the right part first time." },
            { Icon: Truck, title: "Ready Availability", body: "Stocked inventory for the most requested Mahindra, Maruti and Tata parts." },
          ].map(({ Icon, title, body }) => (
            <div
              key={title}
              className="border-r border-b border-border p-8 lg:p-10"
            >
              <div className="h-11 w-11 flex items-center justify-center border border-border text-[#C8102E]">
                <Icon className="h-5 w-5" />
              </div>
              <h4 className="mt-5 font-display font-bold text-lg">{title}</h4>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

export default Brands;
